import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Calendar,
  Clock,
  Plus,
  Trash2,
  Loader2,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { guideAvailabilityAPI } from '../lib/api';
import { cn } from '../lib/utils';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    TIME_OPTIONS.push(`${hour}:${min}`);
  }
}

const GuideAvailabilityCalendar = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: [], tuesday: [], wednesday: [], thursday: [],
    friday: [], saturday: [], sunday: []
  });
  const [exceptions, setExceptions] = useState([]);
  const [defaultAvailable, setDefaultAvailable] = useState(true);
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [exceptionData, setExceptionData] = useState({
    available: false,
    reason: '',
    timeSlots: []
  });

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    try {
      const res = await guideAvailabilityAPI.getAvailability();
      const data = res.data;
      setWeeklySchedule(data.weekly_schedule || {
        monday: [], tuesday: [], wednesday: [], thursday: [],
        friday: [], saturday: [], sunday: []
      });
      setExceptions(data.exceptions || []);
      setDefaultAvailable(data.default_available ?? true);
    } catch (error) {
      console.error('Failed to load availability:', error);
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await guideAvailabilityAPI.updateAvailability({
        weekly_schedule: weeklySchedule,
        exceptions: exceptions,
        default_available: defaultAvailable
      });
      toast.success('Availability saved successfully');
    } catch (error) {
      console.error('Failed to save availability:', error);
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const addTimeSlot = (day) => {
    const slots = weeklySchedule[day] || [];
    const lastSlot = slots[slots.length - 1];
    const newStart = lastSlot ? lastSlot.end : '09:00';
    const newEnd = lastSlot ? 
      TIME_OPTIONS[Math.min(TIME_OPTIONS.indexOf(lastSlot.end) + 2, TIME_OPTIONS.length - 1)] : 
      '18:00';
    
    setWeeklySchedule({
      ...weeklySchedule,
      [day]: [...slots, { start: newStart, end: newEnd }]
    });
  };

  const removeTimeSlot = (day, index) => {
    const slots = [...weeklySchedule[day]];
    slots.splice(index, 1);
    setWeeklySchedule({ ...weeklySchedule, [day]: slots });
  };

  const updateTimeSlot = (day, index, field, value) => {
    const slots = [...weeklySchedule[day]];
    slots[index] = { ...slots[index], [field]: value };
    setWeeklySchedule({ ...weeklySchedule, [day]: slots });
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().split('T')[0];
    const existingException = exceptions.find(e => e.date === dateStr);
    
    if (existingException) {
      setExceptionData({
        available: existingException.available,
        reason: existingException.reason || '',
        timeSlots: existingException.time_slots || []
      });
    } else {
      setExceptionData({
        available: false,
        reason: '',
        timeSlots: []
      });
    }
    setShowExceptionDialog(true);
  };

  const handleSaveException = async () => {
    if (!selectedDate) return;
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    const exception = {
      date: dateStr,
      available: exceptionData.available,
      reason: exceptionData.reason || null,
      time_slots: exceptionData.available && exceptionData.timeSlots.length > 0 ? 
        exceptionData.timeSlots : null
    };
    
    try {
      await guideAvailabilityAPI.addException(exception);
      
      // Update local state
      const newExceptions = exceptions.filter(e => e.date !== dateStr);
      newExceptions.push(exception);
      setExceptions(newExceptions);
      
      setShowExceptionDialog(false);
      toast.success(`${exceptionData.available ? 'Available' : 'Unavailable'} on ${dateStr}`);
    } catch (error) {
      toast.error('Failed to save exception');
    }
  };

  const handleRemoveException = async (dateStr) => {
    try {
      await guideAvailabilityAPI.removeException(dateStr);
      setExceptions(exceptions.filter(e => e.date !== dateStr));
      toast.success('Exception removed');
    } catch (error) {
      toast.error('Failed to remove exception');
    }
  };

  // Calendar rendering helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first of month
    const startDay = firstDay.getDay() || 7; // Make Sunday = 7
    for (let i = 1; i < startDay; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const exception = exceptions.find(e => e.date === dateStr);
    
    if (exception) {
      return exception.available ? 'custom' : 'unavailable';
    }
    
    const dayName = DAYS_OF_WEEK[date.getDay() === 0 ? 6 : date.getDay() - 1];
    const daySlots = weeklySchedule[dayName] || [];
    
    return daySlots.length > 0 ? 'available' : (defaultAvailable ? 'available' : 'unavailable');
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + direction,
      1
    ));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </CardContent>
      </Card>
    );
  }

  const daysInMonth = getDaysInMonth(currentMonth);

  return (
    <div className="space-y-6">
      {/* Weekly Schedule */}
      <Card data-testid="weekly-schedule-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                Weekly Schedule
              </CardTitle>
              <CardDescription>Set your regular working hours for each day</CardDescription>
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {DAYS_OF_WEEK.map((day, dayIndex) => (
            <div key={day} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="w-20 pt-2">
                <span className="font-medium text-slate-700 capitalize">{day}</span>
              </div>
              <div className="flex-1 space-y-2">
                {(weeklySchedule[day] || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-2">No hours set</p>
                ) : (
                  weeklySchedule[day].map((slot, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Select
                        value={slot.start}
                        onValueChange={(v) => updateTimeSlot(day, index, 'start', v)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-slate-400">to</span>
                      <Select
                        value={slot.end}
                        onValueChange={(v) => updateTimeSlot(day, index, 'end', v)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeTimeSlot(day, index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addTimeSlot(day)}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Hours
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Calendar View for Exceptions */}
      <Card data-testid="calendar-exceptions-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-indigo-600" />
            Calendar Exceptions
          </CardTitle>
          <CardDescription>
            Click on a date to mark it as unavailable or set custom hours
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="text-lg font-semibold">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {DAY_LABELS.map(d => (
              <div key={d} className="text-center text-sm font-medium text-slate-500 py-2">
                {d}
              </div>
            ))}
            
            {/* Days */}
            {daysInMonth.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="h-12" />;
              }
              
              const status = getDateStatus(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
              const dateStr = date.toISOString().split('T')[0];
              const hasException = exceptions.some(e => e.date === dateStr);
              
              return (
                <button
                  key={date.toISOString()}
                  onClick={() => !isPast && handleDateClick(date)}
                  disabled={isPast}
                  className={cn(
                    "h-12 rounded-lg text-sm font-medium transition-colors relative",
                    isPast && "opacity-40 cursor-not-allowed",
                    !isPast && "hover:ring-2 hover:ring-indigo-500 cursor-pointer",
                    isToday && "ring-2 ring-indigo-500",
                    status === 'available' && "bg-emerald-100 text-emerald-700",
                    status === 'unavailable' && "bg-red-100 text-red-700",
                    status === 'custom' && "bg-blue-100 text-blue-700"
                  )}
                  data-testid={`calendar-day-${dateStr}`}
                >
                  {date.getDate()}
                  {hasException && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-current" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-100" />
              <span className="text-slate-600">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100" />
              <span className="text-slate-600">Unavailable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-100" />
              <span className="text-slate-600">Custom Hours</span>
            </div>
          </div>

          {/* Exceptions List */}
          {exceptions.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Date Exceptions</h4>
              <div className="space-y-2">
                {exceptions.map((exc) => (
                  <div
                    key={exc.date}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg",
                      exc.available ? "bg-blue-50" : "bg-red-50"
                    )}
                  >
                    <div>
                      <span className="font-medium">
                        {new Date(exc.date).toLocaleDateString('en-US', { 
                          weekday: 'short', month: 'short', day: 'numeric' 
                        })}
                      </span>
                      {exc.reason && (
                        <span className="text-sm text-slate-500 ml-2">- {exc.reason}</span>
                      )}
                      <Badge variant={exc.available ? "outline" : "destructive"} className="ml-2">
                        {exc.available ? 'Custom Hours' : 'Unavailable'}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveException(exc.date)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exception Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Set Availability for {selectedDate?.toLocaleDateString('en-US', { 
                weekday: 'long', month: 'long', day: 'numeric' 
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <Label htmlFor="available-switch">Available this day?</Label>
              <Switch
                id="available-switch"
                checked={exceptionData.available}
                onCheckedChange={(v) => setExceptionData({ ...exceptionData, available: v })}
              />
            </div>
            
            {!exceptionData.available && (
              <div>
                <Label htmlFor="reason">Reason (optional)</Label>
                <Input
                  id="reason"
                  placeholder="e.g., Holiday, Personal day"
                  value={exceptionData.reason}
                  onChange={(e) => setExceptionData({ ...exceptionData, reason: e.target.value })}
                  className="mt-1"
                />
              </div>
            )}
            
            {exceptionData.available && (
              <div>
                <Label>Custom Hours (optional)</Label>
                <p className="text-sm text-slate-500 mb-2">
                  Leave empty to use your regular schedule
                </p>
                {exceptionData.timeSlots.map((slot, index) => (
                  <div key={index} className="flex items-center gap-2 mt-2">
                    <Select
                      value={slot.start}
                      onValueChange={(v) => {
                        const newSlots = [...exceptionData.timeSlots];
                        newSlots[index] = { ...slot, start: v };
                        setExceptionData({ ...exceptionData, timeSlots: newSlots });
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span>to</span>
                    <Select
                      value={slot.end}
                      onValueChange={(v) => {
                        const newSlots = [...exceptionData.timeSlots];
                        newSlots[index] = { ...slot, end: v };
                        setExceptionData({ ...exceptionData, timeSlots: newSlots });
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newSlots = exceptionData.timeSlots.filter((_, i) => i !== index);
                        setExceptionData({ ...exceptionData, timeSlots: newSlots });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setExceptionData({
                    ...exceptionData,
                    timeSlots: [...exceptionData.timeSlots, { start: '09:00', end: '18:00' }]
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Time Slot
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExceptionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveException}>
              <Check className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuideAvailabilityCalendar;
